import { denormalize } from 'normalizr';
import { schema } from 'normalizr';

import {
  articleSchema,
  boardSchema,
  forumSchema,
  personaSchema,
  postSchema,
  searchSchema,
  schoolSchema,
  userSchema,
} from '../schemas';
import { RootState, FirebaseAuth } from '../types/redux';

// Define additional interfaces not in redux.d.ts
interface FirebaseState {
  auth: FirebaseAuth;
}

/*
  firebase state
*/
export const selectFirebaseAuthResults = ({ firebase }: RootState): FirebaseAuth => firebase.auth as FirebaseAuth;
export const selectFirebaseAuthIsLoaded = ({ firebase }: RootState): boolean => firebase.auth.isLoaded;
export const selectFirebaseAuthed = ({ firebase }: RootState): boolean => {
  const {
    auth: { providerData, emailVerified },
  } = firebase;

  let authed = false;
  if (providerData) {
    authed = true;
    const { providerId } = providerData[0];
    if (providerId === 'password' && !emailVerified) {
      authed = false;
    }
  }

  return authed;
};

/*
  auth state
*/
export const selectSignUpResults = ({ auth }: RootState) => auth.signup;

export const selectLoginResults = ({ auth }: RootState) => auth.login;

export const selectForgotPasswordResults = ({ auth }: RootState) => auth.forgotPassword;

/*
  search state
*/
export const selectSearchResults = ({ search }: RootState) => {
  const {
    articles,
    boards,
    entities,
    forums,
    personas,
    posts,
    schools,
    users,
  } = search;
  return denormalize(
    {
      articles,
      boards,
      forums,
      personas,
      posts,
      schools,
      users,
    },
    searchSchema as any,
    entities,
  );
};

/*
  metric state
*/
export const selectAvgPopularTimeResults = ({ metric }: RootState) =>
  (metric as any).avgPopularTime?.data;

export const selectHotArticlesResults = ({ metric }: RootState) => (metric as any).hotArticles?.data;

export const selectHotUsersResults = ({ metric }: RootState) => (metric as any).hotUsers?.data;

export const selectUserGeoDistributionsResults = ({ metric }: RootState) =>
  (metric as any).userGeoDistributions?.data;

/*
  board state
*/
export const selectBoardInfoResults = ({ board }: RootState) => {
  const {
    info: { board: id, entities },
  } = board;
  return denormalize(id, boardSchema, entities);
};

export const selectBoardTopUsers = ({ board }: RootState) => board.topUsers.data;

/*
  article state
*/
export const selectArticleInfoResults = ({ article }: RootState) => {
  const {
    info: { article: id, entities },
  } = article;
  return denormalize(id, articleSchema, entities);
};

/*
  user state
*/
export const selectUserInfoResults = ({ user }: RootState) => {
  const {
    info: { user: id, entities },
  } = user;
  return denormalize(id, userSchema, entities);
};

/*
  persona state
*/
export const selectPersonaInfoResults = ({ persona }: RootState) => {
  const {
    info: { id, entities },
  } = persona;
  return denormalize(id, personaSchema, entities);
};

/*
  forum state
*/
export const selectForumInfoResults = ({ forum }: RootState) => {
  const {
    info: { forum: id, entities },
  } = forum;
  return denormalize(id, forumSchema, entities);
};

/*
  post state
*/
export const selectPostInfoResults = ({ post }: RootState) => {
  const {
    info: { id, entities },
  } = post;
  return denormalize(id, postSchema, entities);
};

/*
  school state
*/
export const selectSchoolInfoResults = ({ school }: RootState) => {
  const {
    info: { id, entities },
  } = school;
  return denormalize(id, schoolSchema, entities);
};

/*
  dashboard state
*/
export const selectDashboardAllFollowings = ({ dashboard }: RootState) =>
  dashboard.followings?.data || [];